import { ListState } from "@/types/list";

import { List, ListPerson, ListPersonResponse } from "../../pages/List/schema/list";

/**
 * `getUnassigned` inbox lifecycle (extracted from `list.slice.ts`).
 *
 * The inbox is populated by a single args-agnostic `getUnassigned` query, so a stale / out-of-order
 * response could otherwise clobber the rows of the tab the user actually switched to. Each request
 * stamps its id on `matchPending`; the fulfilled handler ignores any response that isn't the latest.
 * This replaces the component's old manual `clearInbox()` + lazy-query `abort()` dance, which
 * suppressed the follow-up request entirely on a client-side tab switch.
 *
 * The campaign builder's `getUnassignedAndDummy` uses the same two handlers: an empty list answers
 * with no rows (PREC-4790), so a late answer for the previously opened list must not refill it.
 */

/** The campaign builder's request starts: clear the rows and claim the inbox via its request id. */
export const applyGetUnassignedAndDummyPending = (state: ListState, requestId: string): void => {
	state.inbox = [];
	state.latestInboxRequestId = requestId;
	// These surfaces write the same `inbox`: their request supersedes a failed Messages load.
	state.inboxLoadFailed = false;
};

/**
 * The ordinary lead table's `getUnassigned` starts: it claims the inbox like the builder does, and
 * also retires the builder's scope, so a builder answer still in flight cannot replace the table's
 * rows once it lands (PREC-4790 review r2 M2).
 */
export const applyGetUnassignedPending = (state: ListState, requestId: string): void => {
	applyGetUnassignedAndDummyPending(state, requestId);
	state.activeInboxScope = null;
};

const writeInboxPage = (state: ListState, payload: ListPersonResponse | undefined): void => {
	if (!payload) {
		return;
	}

	if (payload.data) {
		state.inbox = payload.data;
	}

	if (payload.meta) {
		state.meta = payload.meta;
	}
};

/** A request fulfilled: apply it only if it is still the latest (otherwise it is stale). */
export const applyGetUnassignedFulfilled = (
	state: ListState,
	payload: ListPersonResponse | undefined,
	requestId: string
): void => {
	if (requestId !== state.latestInboxRequestId) {
		return;
	}

	writeInboxPage(state, payload);
};

/** The request's arguments as one string, object keys sorted, so equal arguments compare equal. */
export const inboxScopeKey = (args: unknown): string =>
	JSON.stringify(args, (_key, value: unknown) =>
		value && typeof value === "object" && !Array.isArray(value)
			? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
			: value
	);

/**
 * `clearInbox`: empty the panel. Given the request's arguments (the campaign builder passes them
 * on every activation), that request becomes the panel's active scope (PREC-4790 r1).
 */
export const applyInboxCleared = (state: ListState, scopeArgs: object | undefined): void => {
	state.inbox = [];
	// A deliberately cleared inbox is not a failed load; the next request starts from a clean slate.
	state.inboxLoadFailed = false;
	state.activeInboxScope = scopeArgs === undefined ? null : inboxScopeKey(scopeArgs);
};

/**
 * The campaign builder closed (unmounted): its scope stops owning the panel, and the latest-request
 * rule applies again, so its late answer cannot fill the next surface (PREC-4790 review r2 M2).
 * The rows stay: the next surface's own request replaces them.
 */
export const applyInboxScopeReleased = (state: ListState): void => {
	state.activeInboxScope = null;
};

/** The socket's `ALL_DATA` payload (SocketContext): the status says which of the two was found. */
interface LeadContactFound {
	prospectId: number;
	email?: string | null;
	emailStatus?: string | null;
	phone?: string | null;
	phoneStatus?: string | null;
}

/** `addEmailData` (extracted from `list.slice.ts`): a found email or phone onto the lead's prospect. */
export const applyLeadContactFound = (state: ListState, payload: LeadContactFound): void => {
	const prospect = state.inbox.find(
		(person) => person.prospect?.id === payload.prospectId
	)?.prospect;

	if (!prospect) {
		return;
	}

	if (payload.emailStatus) {
		prospect.email = payload.email ?? null;
		prospect.emailStatus = payload.emailStatus;
	} else if (payload.phoneStatus) {
		prospect.phone = payload.phone ?? null;
		prospect.phoneStatus = payload.phoneStatus;
	}
};

/**
 * The campaign builder's `getUnassignedAndDummy` fulfilled. The request id alone is not enough
 * here: going A -> B -> A while A is still in flight dedupes the second A in RTK Query (no new
 * request, no `pending`), so B stays the latest request and its lead would fill A's panel while A's
 * own (empty) answer is dropped. With an active scope, only the answer for that scope's arguments
 * fills the panel; without one, the latest request wins as for `getUnassigned`.
 */
export const applyGetUnassignedAndDummyFulfilled = (
	state: ListState,
	{ payload, meta }: InboxFulfilledAction
): void => {
	if (state.activeInboxScope == null) {
		applyGetUnassignedFulfilled(state, payload, meta.requestId);

		return;
	}

	if (inboxScopeKey(meta.arg.originalArgs) === state.activeInboxScope) {
		writeInboxPage(state, payload);
	}
};

interface InboxFulfilledAction {
	payload: ListPersonResponse | undefined;
	meta: { requestId: string; arg: { originalArgs: unknown } };
}

/** `getAllPersons` fulfilled (extracted from `list.slice.ts`): the search results and their meta. */
export const applyGetAllPersonsFulfilled = (
	state: ListState,
	payload: ListPersonResponse | undefined
): void => {
	if (!payload) {
		return;
	}

	if (payload.data) {
		state.searchResults = payload.data;
	}

	if (payload.meta) {
		state.searchResultsMeta = payload.meta;
	}
};

/**
 * `getAllProspects` inbox lifecycle — the Messages surfaces (Inbox / Outbox / ReadyToSend / Compose)
 * all populate the same `inbox` through this one paginated query. Like `getUnassigned` above, a
 * stale / out-of-order response (e.g. the previous tab's request landing after a client-side tab
 * switch) could otherwise clobber the rows of the tab the user switched to. Each request stamps its
 * id on `matchPending`; the fulfilled handler ignores any response that isn't the latest. This lets
 * the components drop their manual lazy-query `abort()` dance, which suppressed the follow-up request
 * on every other tab switch.
 *
 * Unlike `getUnassigned`, this query paginates: page 1 replaces the rows, later pages append. So
 * `matchPending` only clears the inbox on page 1 (a later page must keep the rows it extends).
 */

/** A new request starts: claim the inbox via its request id; clear the rows only on a fresh page 1. */
export const applyGetAllProspectsPending = (
	state: ListState,
	requestId: string,
	page: number | string | undefined
): void => {
	state.latestInboxRequestId = requestId;
	// A Messages surface owns the inbox now; a builder answer in flight must not land in it.
	state.activeInboxScope = null;
	// A new attempt supersedes whatever the previous one reported (PREC-5088).
	state.inboxLoadFailed = false;

	if (Number(page) === 1) {
		state.inbox = [];
	}
};

/** A request fulfilled: apply it only if it is still the latest; page 1 replaces, later pages append. */
export const applyGetAllProspectsFulfilled = (
	state: ListState,
	payload: ListPersonResponse | undefined,
	requestId: string
): void => {
	if (requestId !== state.latestInboxRequestId) {
		return;
	}

	// The latest request answered, but a bodyless 200 (or one without meta) is not a page: clearing
	// the flag there would re-create the silent empty list this ticket fixes (PREC-5088).
	if (!payload?.data || !payload?.meta) {
		state.inboxLoadFailed = true;

		return;
	}

	state.inboxLoadFailed = false;

	// Convert page to number in case it comes as a string from the API
	const currentPage = Number(payload.meta.page);

	if (currentPage === 1) {
		state.inbox = payload.data;
	} else {
		// New list_person records can be created while the user paginates, which shifts the offset
		// window and makes the API return prospects already shown on earlier pages. Skip any incoming
		// record whose id is already in the inbox to avoid duplicates.
		const existingIds = new Set(state.inbox.map((person) => person.id));
		const newProspects = payload.data.filter((person) => !existingIds.has(person.id));

		state.inbox = [...state.inbox, ...newProspects];
	}

	state.meta = payload.meta;
};

/** The `getAllProspects` rejection's meta, as RTK Query hands it to `matchRejected`. */
export interface GetAllProspectsRejectedMeta {
	requestId: string;
	/** The component aborted its lazy request (e.g. on a client-side tab switch). */
	aborted?: boolean;
	/** The request never ran: its condition said no. */
	condition?: boolean;
}

/**
 * A request rejected: only the latest one may report it (PREC-5088). A failure that the user has
 * already navigated away from — a stale request id, or one the page aborted on a tab switch — says
 * nothing about the surface now on screen, and a skipped request never ran at all.
 */
export const applyGetAllProspectsRejected = (
	state: ListState,
	meta: GetAllProspectsRejectedMeta
): void => {
	if (meta.aborted || meta.condition) {
		return;
	}

	if (meta.requestId !== state.latestInboxRequestId) {
		return;
	}

	state.inboxLoadFailed = true;
};

/**
 * Apply a fulfilled `getList` (full list detail) response (extracted from `list.slice.ts`).
 *
 * `getList` is the only endpoint returning the full source arrays (maps/jobs/csvEnrichments/
 * parallelAi/li*), so its payload is persisted separately into `currentFullList`. The lean
 * `getLeanList` / overview / `updateList` matchers overwrite `currentList` with a
 * source-array-less payload, which would otherwise make re-run source detection
 * (`useListConfigurationStatus`) see "no source". (PREC-3621)
 */
export const applyGetListFulfilled = (state: ListState, payload: List): void => {
	if (!payload?.id) {
		state.currentList = payload;
	}

	const index = state.lists.findIndex((list) => list.id === payload.id);

	if (index !== -1) {
		state.lists[index] = {
			...state.lists[index],
			...payload,
		};
	}

	state.currentList = payload;

	if (payload?.id) {
		state.currentFullList = payload;
	}

	state.inbox.forEach((person, i) => {
		if (person.list?.id === payload.id) {
			state.inbox[i].list = {
				...state.inbox[i].list,
				...payload,
			};
		}
	});
};

/**
 * Removes a lead from the inbox and re-points `currentLeadIndex` so the
 * selection lands on the next lead in the list (or the previous one when the
 * removed lead was last). Shared by the send-message matchers and the
 * `removeFromInbox` reducer — the undo-send commit path relies on the same
 * index arithmetic for its auto-advance behaviour.
 */
export const removeLeadFromInboxState = (state: ListState, listPersonId: number): void => {
	if (state.leavingInboxLeadId === listPersonId) {
		state.leavingInboxLeadId = null;
	}

	const index = state.inbox.findIndex((person) => person.id === listPersonId);

	if (index === -1) {
		return;
	}

	state.inbox = state.inbox.filter((person) => person.id !== listPersonId);

	if (state.inbox.length === 0) {
		state.currentLeadIndex = 0;
	} else if (index < state.currentLeadIndex) {
		state.currentLeadIndex = state.currentLeadIndex - 1;
	} else if (state.currentLeadIndex >= state.inbox.length) {
		state.currentLeadIndex = state.inbox.length - 1;
	}
};

/** `getSingleListPerson` fulfilled: replace the inbox row with the freshly read one, if it is there. */
export const applySingleListPersonToInbox = (state: ListState, payload: ListPerson): void => {
	const index = state.inbox.findIndex((person) => person.id === payload.id);

	if (index === -1) {
		return;
	}

	state.inbox[index] = payload;
};

/** `updateListPersonSenders` fulfilled: mirror the new senders onto the inbox row. */
export const applyListPersonSendersUpdate = (state: ListState, payload: ListPerson): void => {
	const index = state.inbox.findIndex((person) => person.id === payload.id);

	if (index === -1) {
		return;
	}

	state.inbox[index].senders = payload.senders;
};

/** `updateListPersonCampaign` fulfilled: mirror the new campaign fields onto the inbox row. */
export const applyListPersonCampaignUpdate = (state: ListState, payload: ListPerson): void => {
	const index = state.inbox.findIndex((person) => person.id === payload.id);

	if (index === -1) {
		return;
	}

	state.inbox[index].campaignId = payload.campaignId;
	state.inbox[index].campaignConfig = payload.campaignConfig;
	state.inbox[index].lemlist = payload.lemlist;
};
